import { useEffect, useState } from 'react'
import { useReducedMotion } from 'framer-motion'
import { RippleMark } from './ripple-mark'
import { DURATION } from './ripple-animation'

/**
 * Oppstartsbildet: ringvirkningen spilles én gang, og skjermen tones ut i
 * appen. Se `design/brand/DIRECTION-E.md`.
 *
 * Tre regler den holder seg til, og grunnen til hver:
 *
 *  - **Den blokkerer ingenting.** Appen tegnes under hele tiden; dette er et
 *    lokk over, ikke en port foran. Er appen klar først, ser man den i det
 *    lokket tones bort.
 *  - **Én gang per fane, ikke per navigering.** `sessionStorage` gjør at den
 *    hører til det å åpne appen, ikke det å bytte side i den. Uten den ville
 *    hver ruteendring som remonterer skallet spilt den på nytt.
 *  - **Aldri ved redusert bevegelse.** Da vises den ikke i det hele tatt,
 *    ikke bare uten animasjon: hele poenget med skjermen ER bevegelsen.
 *
 * Den monteres i appskallet, ikke i roten, så en førstegangsbesøkende på en
 * delt lenke slipper 2,6 sekunder med merkevare de ikke har bedt om.
 */

const SEEN_KEY = 'er:splash-seen'
const FADE_MS = 420

function alreadySeen(): boolean {
  try {
    return sessionStorage.getItem(SEEN_KEY) === '1'
  } catch {
    // privat modus uten lagring: vis den, heller én gang for mye enn å krasje
    return false
  }
}

function markSeen() {
  try {
    sessionStorage.setItem(SEEN_KEY, '1')
  } catch {
    /* lagring utilgjengelig, ikke noe å gjøre */
  }
}

export function BrandSplash() {
  const prefersReducedMotion = useReducedMotion()
  const [show, setShow] = useState(() => !alreadySeen())
  const [leaving, setLeaving] = useState(false)

  useEffect(() => {
    if (!show) return
    if (prefersReducedMotion) {
      setShow(false)
      markSeen()
      return
    }
    markSeen()
    const out = window.setTimeout(() => setLeaving(true), DURATION)
    const gone = window.setTimeout(() => setShow(false), DURATION + FADE_MS)
    return () => {
      window.clearTimeout(out)
      window.clearTimeout(gone)
    }
  }, [show, prefersReducedMotion])

  if (!show) return null

  return (
    <div
      aria-hidden="true"
      className="fixed inset-0 z-[var(--z-splash,90)] grid place-items-center"
      style={{
        // Identity tokens, never the color ramp: an event theme or org brand
        // can overwrite --color-primary-* WHILE the splash is on screen (the
        // fetch races the 2.6s play), and a generated dark theme put the
        // «Ripple» word at 1.1:1 on this very background. --er-brand-* is
        // the one set no injection pipeline may write.
        background:
          'linear-gradient(165deg, var(--er-brand-splash-from) 0%, var(--er-brand-splash-mid) 46%, var(--er-brand-splash-to) 100%)',
        opacity: leaving ? 0 : 1,
        transition: `opacity ${FADE_MS}ms ease-in`,
        pointerEvents: leaving ? 'none' : 'auto',
      }}
    >
      <div className="flex flex-col items-center gap-5">
        <RippleMark size={96} cut="full" playOnMount className="text-[var(--text-public-primary)]" />
        <span
          className="text-h2 leading-none tracking-tight text-[var(--text-public-primary)]"
          style={{
            opacity: leaving ? 0 : 1,
            animation: 'splash-word 620ms ease-out 900ms both',
          }}
        >
          <span className="font-extrabold">Event</span>
          <span className="font-light text-[var(--er-brand-splash-word)]">Ripple</span>
        </span>
      </div>
    </div>
  )
}
